import { getRegionPathFromName } from "./routeSlugs";
import { REGION_MAP } from "./regions";

export const HOME_BASE_URL = "https://onalark.org";
export const SHARE_IMAGE_URL = `${HOME_BASE_URL}/og-image.png`;
export const BRAND_LOGO_URL = `${HOME_BASE_URL}/favicon.png`;

export const HOME_PAGE_TITLE = "Albany & Capital District Events Calendar | On A Lark";
export const HOME_PAGE_DESCRIPTION =
  "Local events in Albany and the surrounding Capital District, from Troy and Schenectady to Saratoga: concerts, festivals, arts, family events, and more.";
export const HOMEPAGE_H1 = "Events Calendar for Albany & the Surrounding Capital District";
export const HOMEPAGE_INTRO =
  "On A Lark is a free events calendar for Albany and the surrounding Capital District, bringing together live music, nightlife, community events, classes, festivals, and family-friendly things to do across Albany, Troy, Schenectady, Saratoga, and nearby towns. Browse thousands of local events in one place, then connect directly to the original publishers for full details.";
/** Derived from REGION_MAP in shared/regions.ts — the single source of truth. */
export const HOMEPAGE_REGIONS = Object.keys(REGION_MAP) as ReadonlyArray<keyof typeof REGION_MAP>;

export type CategorySeoConfig = {
  name: string;
  slug: string;
  title: string;
  description: string;
};

export const CATEGORY_SEO_CONFIGS: readonly CategorySeoConfig[] = [
  {
    name: "Dining, Drinks, Music & Nightlife",
    slug: "dining-drinks-music-nightlife",
    title: "Dining, Drinks, Music & Nightlife Events in Albany & the Capital District | On A Lark",
    description:
      "Find upcoming dining, drinks, live music, nightlife, open mics, concerts, and food events across Albany, Troy, Schenectady, Saratoga, and the rest of the Capital District.",
  },
  {
    name: "Arts & Theater",
    slug: "arts-theater",
    title: "Arts & Theater Events in Albany & the Capital District | On A Lark",
    description:
      "Find upcoming theater, comedy, gallery shows, dance, museums, arts events, and performances across Albany, Troy, Schenectady, Saratoga, and the rest of the Capital District.",
  },
  {
    name: "Family & Kids",
    slug: "family-kids",
    title: "Family & Kids Events in Albany & the Capital District | On A Lark",
    description:
      "Find family-friendly events, kids activities, library programs, festivals, workshops, and weekend things to do across Albany and the surrounding Capital District.",
  },
  {
    name: "Sports & Recreation",
    slug: "sports-recreation",
    title: "Sports & Recreation Events in Albany & the Capital District | On A Lark",
    description:
      "Find fitness classes, yoga, races, hikes, skating, outdoor activities, and recreational events across Albany, Troy, Schenectady, Saratoga, and the rest of the Capital District.",
  },
  {
    name: "Community & Social",
    slug: "community-social",
    title: "Community & Social Events in Albany & the Capital District | On A Lark",
    description:
      "Find markets, meetups, volunteer events, fundraisers, civic events, social gatherings, and neighborhood happenings across Albany and the surrounding Capital District.",
  },
  {
    name: "Classes & Workshops",
    slug: "classes-workshops",
    title: "Classes & Workshops in Albany & the Capital District | On A Lark",
    description:
      "Find creative classes, wellness workshops, educational programs, professional learning, and hands-on activities across Albany and the surrounding Capital District.",
  },
  {
    name: "University & College",
    slug: "university-college",
    title: "University & College Events in Albany & the Capital District | On A Lark",
    description:
      "Find college, campus, lecture, student, arts, sports, and community events from universities across Albany and the surrounding Capital District.",
  },
] as const;

export type DateIntentKey = "today" | "tomorrow" | "weekend";

export type DateIntentSeoConfig = {
  key: DateIntentKey;
  path: string;
  linkLabel: string;
  title: string;
  description: string;
  h1: string;
  intro: string;
  collectionName: string;
};

// Indexable date-intent landing pages. These are stable URLs with rotating
// event content ("this weekend" always shows the upcoming weekend), matching
// how people actually search for local events.
export const DATE_INTENT_SEO_CONFIGS: readonly DateIntentSeoConfig[] = [
  {
    key: "today",
    path: "/today",
    linkLabel: "Today",
    title: "Things to Do Today in Albany & the Capital District | On A Lark",
    description:
      "What's happening today in Albany and the surrounding Capital District? Browse today's concerts, family activities, nightlife, and community events, updated all day.",
    h1: "Things to Do Today in Albany & the Surrounding Capital District",
    intro:
      "Looking for something to do today? On A Lark gathers today's concerts, shows, family activities, classes, nightlife, and community events from across Albany and the surrounding Capital District, including Troy, Schenectady, and Saratoga — all in one place, updated throughout the day.",
    collectionName: "Events Today",
  },
  {
    key: "tomorrow",
    path: "/tomorrow",
    linkLabel: "Tomorrow",
    title: "Things to Do Tomorrow in Albany & the Capital District | On A Lark",
    description:
      "Planning ahead? Browse tomorrow's concerts, shows, family activities, and community events across Albany and the surrounding Capital District on On A Lark.",
    h1: "Things to Do Tomorrow in Albany & the Surrounding Capital District",
    intro:
      "Planning tomorrow? On A Lark gathers tomorrow's concerts, shows, family activities, classes, nightlife, and community events from across Albany and the surrounding Capital District, including Troy, Schenectady, and Saratoga, so you can lock in plans ahead of time.",
    collectionName: "Events Tomorrow",
  },
  {
    key: "weekend",
    path: "/this-weekend",
    linkLabel: "This Weekend",
    title: "Things to Do This Weekend in Albany & the Capital District | On A Lark",
    description:
      "Find things to do this weekend in Albany and the surrounding Capital District: concerts, festivals, markets, family fun, and nightlife, updated daily.",
    h1: "Things to Do This Weekend in Albany & the Surrounding Capital District",
    intro:
      "Looking for weekend plans? On A Lark rounds up this weekend's concerts, festivals, markets, family activities, nightlife, and community events from across Albany and the surrounding Capital District, including Troy, Schenectady, and Saratoga — updated daily as new events are published.",
    collectionName: "Events This Weekend",
  },
] as const;

export function getDateIntentSeoConfigByKey(key: string | null | undefined): DateIntentSeoConfig | undefined {
  if (!key) return undefined;
  return DATE_INTENT_SEO_CONFIGS.find((config) => config.key === key);
}

export function getDateIntentSeoConfigByPath(pathname: string | null | undefined): DateIntentSeoConfig | undefined {
  const normalized = pathname?.trim().toLowerCase().replace(/\/+$/, "") || "";
  if (!normalized) return undefined;
  return DATE_INTENT_SEO_CONFIGS.find((config) => config.path === normalized);
}

// Region landing-page metadata: keyword-first title, brand last. Shared so the
// client Helmet path and server snapshot path cannot drift apart.
export function getRegionPageTitle(regionName: string): string {
  return `${regionName} Events & Things to Do | On A Lark`;
}

export function getRegionPageDescription(regionName: string): string {
  return `Things to do in ${regionName}: browse upcoming concerts, festivals, family activities, nightlife, and community events across the Capital District on On A Lark.`;
}

// Region buckets that aren't towns ("Unknown", "Outside Capital District",
// "Virtual/Online"). Their pages stay reachable but are kept out of search engines
// and the sitemap.
const NON_TOWN_REGION_KEYS = new Set(["unknown", "outsidecapitaldistrict", "virtualonline"]);

export function isNonTownRegion(regionName: string): boolean {
  return NON_TOWN_REGION_KEYS.has(regionName.toLowerCase().replace(/[^a-z0-9]/g, ""));
}

// Region x date-intent landing pages (/regions/:slug/today|tomorrow|this-weekend).
// Stable URLs with rotating event content, like the global date-intent pages.
export type RegionDateIntentMeta = {
  title: string;
  description: string;
  h1: string;
  intro: string;
  collectionName: string;
};

export function getRegionDateIntentPath(regionName: string, intent: DateIntentSeoConfig): string {
  return `${getRegionPathFromName(regionName)}${intent.path}`;
}

export function buildRegionDateIntentMeta(regionName: string, intent: DateIntentSeoConfig): RegionDateIntentMeta {
  const phrase = intent.linkLabel.toLowerCase(); // "today" | "tomorrow" | "this weekend"
  return {
    title: `Things to Do in ${regionName} ${intent.linkLabel} | On A Lark`,
    description: `Find things to do ${phrase} in ${regionName}, NY: concerts, live music, family activities, nightlife, and community events on the On A Lark local calendar.`,
    h1: `Things to Do in ${regionName} ${intent.linkLabel}`,
    intro: `Looking for something to do ${phrase} in ${regionName}? On A Lark gathers ${regionName} concerts, shows, family activities, classes, nightlife, and community events in one place — with the rest of the Capital District one click away.`,
    collectionName: `${regionName} Events ${intent.linkLabel}`,
  };
}

export function getCategoryPathFromName(categoryName: string): string {
  const configured = getCategorySeoConfigByName(categoryName);
  return `/categories/${configured?.slug ?? slugifyForSeoPath(categoryName)}`;
}

export function getCategorySeoConfigByName(categoryName: string | null | undefined): CategorySeoConfig | undefined {
  const normalized = categoryName?.trim().toLowerCase();
  if (!normalized) return undefined;
  return CATEGORY_SEO_CONFIGS.find((config) => config.name.toLowerCase() === normalized);
}

export function getCategorySeoConfigBySlug(categorySlug: string | null | undefined): CategorySeoConfig | undefined {
  const normalized = categorySlug?.trim().toLowerCase();
  if (!normalized) return undefined;
  return CATEGORY_SEO_CONFIGS.find((config) => config.slug === normalized);
}

function slugifyForSeoPath(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "On A Lark",
  url: `${HOME_BASE_URL}/`,
  description: HOME_PAGE_DESCRIPTION,
  publisher: {
    "@type": "Organization",
    name: "On A Lark",
    url: `${HOME_BASE_URL}/`,
    logo: BRAND_LOGO_URL,
  },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${HOME_BASE_URL}/?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
} as const;

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "On A Lark",
  url: `${HOME_BASE_URL}/`,
  logo: BRAND_LOGO_URL,
  description:
    "Free local event discovery across Albany, Troy, Schenectady, Saratoga, and the wider Capital District.",
  areaServed: [
    "Albany, NY",
    "Troy, NY",
    "Schenectady, NY",
    "Saratoga Springs, NY",
    "Hudson Valley, NY",
  ],
} as const;

type BreadcrumbItem = {
  name: string;
  url?: string;
};

type EventSchemaInput = {
  name: string;
  startDate: string | Date | null | undefined;
  endDate?: string | Date | null;
  time?: string | null;
  description: string;
  url: string;
  venue?: string | null;
  region?: string | null;
  image?: string;
  organizerName?: string | null;
  organizerUrl?: string | null;
  performer?: string | null;
};

type PlaceSchemaInput = {
  name: string;
  url: string;
  region?: string | null;
  address?: string | null;
  phone?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
};

function compactObject<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => entryValue !== undefined && entryValue !== null && entryValue !== ""),
  ) as T;
}

function structuredDataUrl(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return undefined;
    return parsed.href;
  } catch {
    return undefined;
  }
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) =>
      compactObject({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: structuredDataUrl(item.url),
      }),
    ),
  };
}

function extractDatePart(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 10);
  }

  const isoDate = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return isoDate?.[1] ?? null;
}

function parseStartTime(time: string | null | undefined): { hour: number; minute: number } | null {
  if (!time) return null;
  const normalized = time.toLowerCase();
  if (normalized.includes("all day") || normalized.includes("tbd") || normalized.includes("see source")) {
    return null;
  }

  const match = normalized.match(/\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/);
  if (!match) return null;

  let hour = Number.parseInt(match[1], 10);
  const minute = match[2] ? Number.parseInt(match[2], 10) : 0;
  const period = match[3];
  if (Number.isNaN(hour) || Number.isNaN(minute) || hour > 23 || minute > 59) return null;

  if (period === "pm" && hour < 12) hour += 12;
  if (period === "am" && hour === 12) hour = 0;
  if (!period && hour > 23) return null;

  return { hour, minute };
}

function getEasternOffset(datePart: string): string {
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return "-05:00";

  const sample = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const timeZoneName = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    timeZoneName: "shortOffset",
  })
    .formatToParts(sample)
    .find((part) => part.type === "timeZoneName")?.value;
  const match = timeZoneName?.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (!match) return "-05:00";

  const sign = match[1];
  const hours = match[2].padStart(2, "0");
  const minutes = (match[3] ?? "00").padStart(2, "0");
  return `${sign}${hours}:${minutes}`;
}

function formatSchemaDate(value: string | Date | null | undefined, time?: string | null): string | Date | null | undefined {
  const datePart = extractDatePart(value);
  const parsedTime = parseStartTime(time);
  if (datePart && parsedTime) {
    const hour = String(parsedTime.hour).padStart(2, "0");
    const minute = String(parsedTime.minute).padStart(2, "0");
    return `${datePart}T${hour}:${minute}:00${getEasternOffset(datePart)}`;
  }

  return datePart ?? value;
}

export function buildEventSchema(input: EventSchemaInput) {
  const startDate = formatSchemaDate(input.startDate, input.time);
  const eventUrl = structuredDataUrl(input.url);
  const imageUrl = structuredDataUrl(input.image);
  if (!input.name || !startDate || !input.venue || !eventUrl) {
    return null;
  }

  return compactObject({
    "@context": "https://schema.org",
    "@type": "Event",
    name: input.name,
    startDate,
    endDate: formatSchemaDate(input.endDate, input.time),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    description: input.description,
    url: eventUrl,
    image: imageUrl ? [imageUrl] : undefined,
    location: input.venue
      ? compactObject({
          "@type": "Place",
          name: input.venue,
          address: input.region
            ? {
                "@type": "PostalAddress",
                addressLocality: input.region,
                addressRegion: "NY",
                addressCountry: "US",
              }
            : undefined,
        })
      : undefined,
    organizer: input.organizerName
      ? compactObject({
          "@type": "Organization",
          name: input.organizerName,
          url: structuredDataUrl(input.organizerUrl),
        })
      : undefined,
    performer: input.performer
      ? {
          "@type": "PerformingGroup",
          name: input.performer,
        }
      : undefined,
  });
}

export function buildPlaceSchema(input: PlaceSchemaInput) {
  return compactObject({
    "@context": "https://schema.org",
    "@type": "Place",
    name: input.name,
    url: structuredDataUrl(input.url),
    address: input.region || input.address
      ? compactObject({
          "@type": "PostalAddress",
          streetAddress: input.address,
          addressLocality: input.region,
          addressRegion: "NY",
          addressCountry: "US",
        })
      : undefined,
    telephone: input.phone,
    geo: input.latitude && input.longitude
      ? {
          "@type": "GeoCoordinates",
          latitude: input.latitude,
          longitude: input.longitude,
        }
      : undefined,
  });
}
